/**
 * UltraLife Trades — Market & Trade Context
 *
 * Provides the current market and trade to the entire app.
 * Resolved from URL path, query params, or geolocation.
 *
 * URL patterns supported:
 *   /boise/decks              → market=boise, trade=decks
 *   /boise                    → market=boise, trade=decks (default)
 *   ?market=boise&trade=decks → same via query params (for FB ads)
 *   /                         → geolocation detection → nearest market
 */

import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { getMarket, getNearestMarket, DEFAULT_MARKET } from "../config/markets";
import { getTrade, DEFAULT_TRADE } from "../config/trades";

const MarketContext = createContext(null);

export function MarketProvider({ children }) {
  const [market, setMarket] = useState(null);
  const [trade, setTrade] = useState(null);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoAttempted, setGeoAttempted] = useState(false);

  /**
   * Resolve market + trade from current URL.
   * Called on mount and when the URL changes.
   */
  const resolveFromURL = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    const pathname = window.location.pathname;

    // 1. Try query params first (?market=boise&trade=decks) — used by Facebook ads
    const qMarket = params.get("market");
    const qTrade = params.get("trade");

    if (qMarket) {
      const m = getMarket(qMarket);
      if (m) {
        // Only allow trades the market actually offers (hq#19 compliance gate)
        const cand = getTrade(qTrade);
        const t = cand && m.trades.includes(cand.slug) ? cand : DEFAULT_TRADE;
        setMarket(m);
        setTrade(t);
        return true;
      }
      // Unknown slug from ad param — log so typos surface in console/Sentry
      // eslint-disable-next-line no-console
      console.warn(
        `[MarketContext] Unknown market slug "${qMarket}" in ?market= param; falling back to geolocation.`,
      );
    }

    // 2. Try URL path (/:market/:trade or /:market)
    // Strip leading slash, split by /
    const parts = pathname.replace(/^\//, "").split("/").filter(Boolean);
    if (parts.length >= 1) {
      const m = getMarket(parts[0]);
      if (m) {
        // Only allow trades the market actually offers (hq#19 compliance gate)
        const cand = parts.length >= 2 ? getTrade(parts[1]) : null;
        const t = cand && m.trades.includes(cand.slug) ? cand : DEFAULT_TRADE;
        setMarket(m);
        setTrade(t);
        return true;
      }
      // Path looked like a market slug but didn't match anything — log it.
      // Known non-market paths bypass this via App.js pathname checks (/work-with-us, /booking-confirmed).
      // eslint-disable-next-line no-console
      console.warn(
        `[MarketContext] Unknown market slug "${parts[0]}" in URL path; falling back to geolocation.`,
      );
    }

    return false; // no market found in URL
  }, []);

  /**
   * Attempt geolocation and find nearest market.
   */
  const resolveFromGeolocation = useCallback(() => {
    if (!navigator.geolocation || geoAttempted) return;
    setGeoAttempted(true);
    setGeoLoading(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const nearest = getNearestMarket(latitude, longitude);
        setMarket(nearest || DEFAULT_MARKET);
        setTrade(DEFAULT_TRADE);
        setGeoLoading(false);
      },
      () => {
        // Geolocation denied or failed — use default
        setMarket(DEFAULT_MARKET);
        setTrade(DEFAULT_TRADE);
        setGeoLoading(false);
      },
      // maximumAge: 60s — stale fix caused people to land in the wrong market
      // after driving or using a VPN; 5min cache (300000) was too long.
      { timeout: 5000, maximumAge: 60000 }
    );
  }, [geoAttempted]);

  // On mount: try URL first, then geolocation, then default
  useEffect(() => {
    const foundInURL = resolveFromURL();
    if (!foundInURL) {
      resolveFromGeolocation();
    }
  }, [resolveFromURL, resolveFromGeolocation]);

  // If geolocation never fires (no browser support), fall back to default
  useEffect(() => {
    if (!navigator.geolocation && !market) {
      setMarket(DEFAULT_MARKET);
      setTrade(DEFAULT_TRADE);
    }
  }, [market]);

  /**
   * Navigate to a different market and/or trade.
   * Updates the URL and local state.
   */
  const navigate = useCallback((newMarketSlug, newTradeSlug) => {
    const m = getMarket(newMarketSlug) || DEFAULT_MARKET;
    const t = getTrade(newTradeSlug) || DEFAULT_TRADE;
    setMarket(m);
    setTrade(t);

    // Update URL path without page reload
    const newPath = `/${m.slug}/${t.slug}`;
    window.history.pushState({}, "", newPath);
  }, []);

  /**
   * Switch just the trade while keeping the market.
   */
  const switchTrade = useCallback((newTradeSlug) => {
    const t = getTrade(newTradeSlug) || DEFAULT_TRADE;
    setTrade(t);
    if (market) {
      const newPath = `/${market.slug}/${t.slug}`;
      window.history.pushState({}, "", newPath);
    }
  }, [market]);

  const value = {
    market,
    trade,
    geoLoading,
    navigate,
    switchTrade,
    isReady: !!market && !!trade,
  };

  return (
    <MarketContext.Provider value={value}>
      {children}
    </MarketContext.Provider>
  );
}

export function useMarket() {
  const ctx = useContext(MarketContext);
  if (!ctx) throw new Error("useMarket must be used within a MarketProvider");
  return ctx;
}
